import { useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import AppLayout from '../../components/layout/AppLayout'
import KpiCard from '../../components/dashboard/KpiCard'
import ColumnHeaderFilter from '../../components/inactive/ColumnHeaderFilter'
import MobileInactiveList from '../../components/inactive/mobile/MobileInactiveList'
import { useAdminGuard } from '../../hooks/useAdminGuard'
import { useInactiveProducts } from '../../hooks/useInactiveProducts'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useLocalStorageState } from '../../hooks/useLocalStorageState'
import { useToast } from '../../hooks/useToast'

function brl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

const ENCALHE_DAYS = 90

type SortKey = 'sku' | 'name' | 'supplier' | 'current_stock' | 'cost' | 'last_sale_at'

interface ColumnFilters {
  suppliers: string[]
  stockMin: number | null
  stockMax: number | null
  costMin: number | null
  costMax: number | null
  lastSaleFrom: string | null
  lastSaleTo: string | null
}

const EMPTY_FILTERS: ColumnFilters = {
  suppliers: [],
  stockMin: null,
  stockMax: null,
  costMin: null,
  costMax: null,
  lastSaleFrom: null,
  lastSaleTo: null,
}

export default function InactivePage() {
  const isMobile = useIsMobile()
  const { products, loading, error, reactivate, softDelete } = useInactiveProducts()
  const { isAdmin } = useAdminGuard()
  const [search, setSearch] = useState('')
  const [encalheOnly, setEncalheOnly] = useState(false)
  const [reactivating, setReactivating] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [filters, setFilters] = useLocalStorageState<ColumnFilters>('bx:inactive-filters:v1', EMPTY_FILTERS)
  const [sortKey, setSortKey] = useLocalStorageState<SortKey>('bx:inactive-sort-key:v1', 'name')
  const [sortDir, setSortDir] = useLocalStorageState<'asc' | 'desc'>('bx:inactive-sort-dir:v1', 'asc')
  const { notify } = useToast()

  const supplierOptions = useMemo(() => {
    const set = new Set<string>()
    products.forEach(p => { if (p.suppliers?.name) set.add(p.suppliers.name) })
    return Array.from(set).sort()
  }, [products])

  // Encalhe: mesma régua do dashboard — saldo > 0 + tem histórico de venda + última venda há +90 dias.
  // Itens que nunca venderam NÃO contam (consistência com EstoqueDashboardPage).
  // useState initializer = call-once-at-mount, compatível com regra react-hooks/purity.
  const [encalheCutoffMs] = useState(() => Date.now() - ENCALHE_DAYS * 24 * 60 * 60 * 1000)
  const isEncalhe = (p: typeof products[number]) =>
    p.current_stock > 0 && !!p.last_sale_at && new Date(p.last_sale_at).getTime() < encalheCutoffMs

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const supplierSet = new Set(filters.suppliers)
    const fromMs = filters.lastSaleFrom ? new Date(filters.lastSaleFrom).getTime() : null
    // O 'até' é inclusivo até o final do dia.
    const toMs = filters.lastSaleTo ? new Date(filters.lastSaleTo).getTime() + 24 * 60 * 60 * 1000 - 1 : null

    return products.filter(p => {
      if (q && !p.sku.toLowerCase().includes(q) && !p.name.toLowerCase().includes(q)) return false
      if (supplierSet.size > 0) {
        const name = p.suppliers?.name ?? ''
        if (!supplierSet.has(name)) return false
      }
      if (filters.stockMin != null && p.current_stock < filters.stockMin) return false
      if (filters.stockMax != null && p.current_stock > filters.stockMax) return false
      const cost = p.cost ?? 0
      if (filters.costMin != null && cost < filters.costMin) return false
      if (filters.costMax != null && cost > filters.costMax) return false
      if (fromMs != null || toMs != null) {
        const ts = p.last_sale_at ? new Date(p.last_sale_at).getTime() : null
        if (ts == null) return false
        if (fromMs != null && ts < fromMs) return false
        if (toMs != null && ts > toMs) return false
      }
      if (encalheOnly && !isEncalhe(p)) return false
      return true
    })
  }, [products, search, filters, encalheOnly, encalheCutoffMs])

  const sorted = useMemo(() => {
    const list = [...filtered]
    const dir = sortDir === 'asc' ? 1 : -1
    list.sort((a, b) => {
      const va = sortValue(a, sortKey)
      const vb = sortValue(b, sortKey)
      if (va < vb) return -1 * dir
      if (va > vb) return 1 * dir
      return 0
    })
    return list
  }, [filtered, sortKey, sortDir])

  // Valor parado: produtos inativos que ainda têm saldo em estoque.
  // Zerados não somam (catálogo desativado sem capital imobilizado).
  const stockedInactive = useMemo(
    () => products.filter(p => p.current_stock > 0),
    [products]
  )
  const stockedValue = useMemo(
    () => stockedInactive.reduce((sum, p) => sum + p.current_stock * (p.cost ?? 0), 0),
    [stockedInactive]
  )

  // Encalhe: itens inativos parados há +90 dias
  const encalheItems = useMemo(
    () => products.filter(isEncalhe),
    [products, encalheCutoffMs]
  )
  const encalheValue = useMemo(
    () => encalheItems.reduce((sum, p) => sum + p.current_stock * (p.cost ?? 0), 0),
    [encalheItems]
  )

  const hasColumnFilter =
    filters.suppliers.length > 0 ||
    filters.stockMin != null || filters.stockMax != null ||
    filters.costMin != null || filters.costMax != null ||
    filters.lastSaleFrom != null || filters.lastSaleTo != null

  function clearFilters() {
    setFilters(EMPTY_FILTERS)
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  function toggleEncalhe() {
    setEncalheOnly(v => !v)
  }

  async function handleReactivate(id: string, name: string) {
    if (!confirm(`Reativar "${name}"? Voltará para o Painel BX.`)) return
    setReactivating(id)
    try {
      await reactivate(id)
      notify('success', `"${name}" reativado.`)
    } catch (e) {
      notify('error', `Erro: ${(e as Error).message}`)
    } finally {
      setReactivating(null)
    }
  }

  async function handleDelete(id: string, name: string, sku: string) {
    const ok = confirm(
      `Excluir definitivamente "${name}" (SKU ${sku})?\n\n` +
      `O produto vai sumir de todas as telas (Painel BX, Inativos, Cadastro, Fornecedor).\n` +
      `O histórico no banco é preservado para auditoria — esta ação não apaga vendas, ` +
      `movimentações de estoque nem pedidos.\n\nEsta ação não pode ser desfeita pela UI.`
    )
    if (!ok) return
    setDeleting(id)
    try {
      await softDelete(id)
      notify('success', `"${name}" excluído definitivamente.`)
    } catch (e) {
      notify('error', `Erro: ${(e as Error).message}`)
    } finally {
      setDeleting(null)
    }
  }

  function handleExport() {
    // Respeita filtros aplicados (search, fornecedor, faixa) e adiciona o filtro de saldo > 0
    const items = sorted.filter(p => p.current_stock > 0)
    if (items.length === 0) {
      notify('error', 'Nenhum produto inativo com saldo nos filtros atuais.')
      return
    }
    const data = items.map(p => ({
      SKU: p.sku,
      Produto: p.name,
      Fornecedor: p.suppliers?.name ?? '',
      'Estoque atual': p.current_stock,
      'Custo unitário': p.cost,
      'Valor total': p.current_stock * (p.cost ?? 0),
      'Última venda': p.last_sale_at ? fmtDate(p.last_sale_at) : 'Sem venda',
      Encalhe: isEncalhe(p) ? 'Sim' : 'Não',
    }))
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Inativos com saldo')
    XLSX.writeFile(wb, `inativos_com_saldo_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  if (isMobile) {
    return (
      <AppLayout title="Produtos Inativos">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center text-red-400 py-16 text-sm">{error}</div>
        ) : (
          <MobileInactiveList
            products={products}
            isAdmin={isAdmin}
            reactivating={reactivating}
            deleting={deleting}
            onReactivate={handleReactivate}
            onDelete={handleDelete}
          />
        )}
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Produtos Inativos">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-base-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-64 text-red-400 text-sm">
          Erro ao carregar dados: {error}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-surface border border-line rounded-xl p-3 flex flex-wrap gap-3 items-center">
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar SKU ou produto..."
              className="flex-1 min-w-0 sm:min-w-[220px] bg-paper border border-line rounded px-3 py-1.5 text-ink text-sm placeholder:text-ink-faint"
            />
            {hasColumnFilter && (
              <button
                onClick={clearFilters}
                className="text-xs px-3 py-1.5 bg-sunken hover:bg-line text-ink-muted rounded border border-line"
              >
                Limpar filtros
              </button>
            )}
            <button
              onClick={handleExport}
              disabled={stockedInactive.length === 0}
              className="sm:ml-auto px-3 py-1.5 bg-sunken hover:bg-line disabled:opacity-40 text-ink text-xs font-medium rounded-lg transition-colors"
              title="Baixa Excel apenas com produtos que têm saldo"
            >
              ⬇ Exportar com saldo
            </button>
            <div className="px-3 py-1.5 rounded-lg text-xs font-medium bg-sunken text-ink-muted border border-line">
              {sorted.length === products.length
                ? `Total inativos: ${products.length}`
                : `${sorted.length} de ${products.length}`}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:max-w-2xl">
            <KpiCard
              label="Valor parado em estoque"
              value={brl(stockedValue)}
              tone="warning"
              hint={`${stockedInactive.length} produto(s) inativo(s) com saldo`}
            />
            <KpiCard
              label="Encalhe (+90 dias sem venda)"
              value={brl(encalheValue)}
              tone="danger"
              hint={encalheOnly ? 'clique para limpar filtro' : `${encalheItems.length} produto(s) · clique para filtrar`}
              onClick={toggleEncalhe}
              active={encalheOnly}
            />
          </div>

          <div className="bg-surface border border-line rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-sunken border-b border-line sticky top-0">
                  <tr>
                    <SortHeader label="Código" align="left" active={sortKey === 'sku'} dir={sortDir} onClick={() => toggleSort('sku')} />
                    <SortHeader label="Produto" align="left" active={sortKey === 'name'} dir={sortDir} onClick={() => toggleSort('name')} />
                    <ColumnHeaderFilter
                      kind="categorical"
                      label="Fornecedor"
                      align="left"
                      sortDir={sortKey === 'supplier' ? sortDir : undefined}
                      onSort={() => toggleSort('supplier')}
                      options={supplierOptions}
                      selected={new Set(filters.suppliers)}
                      onChange={next => setFilters({ ...filters, suppliers: Array.from(next) })}
                    />
                    <ColumnHeaderFilter
                      kind="numeric"
                      label="Estoque Atual"
                      align="right"
                      sortDir={sortKey === 'current_stock' ? sortDir : undefined}
                      onSort={() => toggleSort('current_stock')}
                      min={filters.stockMin}
                      max={filters.stockMax}
                      onChange={(min, max) => setFilters({ ...filters, stockMin: min, stockMax: max })}
                    />
                    <ColumnHeaderFilter
                      kind="numeric"
                      label="Custo"
                      align="right"
                      step={0.01}
                      sortDir={sortKey === 'cost' ? sortDir : undefined}
                      onSort={() => toggleSort('cost')}
                      min={filters.costMin}
                      max={filters.costMax}
                      onChange={(min, max) => setFilters({ ...filters, costMin: min, costMax: max })}
                    />
                    <ColumnHeaderFilter
                      kind="date"
                      label="Última Venda"
                      align="right"
                      sortDir={sortKey === 'last_sale_at' ? sortDir : undefined}
                      onSort={() => toggleSort('last_sale_at')}
                      from={filters.lastSaleFrom}
                      to={filters.lastSaleTo}
                      onChange={(from, to) => setFilters({ ...filters, lastSaleFrom: from, lastSaleTo: to })}
                    />
                    <th className="text-right text-xs font-semibold text-ink-muted uppercase tracking-wider px-4 py-2.5">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(p => (
                    <tr key={p.id} className="border-b border-line hover:bg-sunken text-ink-muted">
                      <td className="px-4 py-2.5 font-mono text-xs">{p.sku}</td>
                      <td className="px-4 py-2.5">{p.name}</td>
                      <td className="px-4 py-2.5 max-w-[260px] truncate" title={p.suppliers?.name ?? ''}>
                        {p.suppliers?.name ?? '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{p.current_stock}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {p.cost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                        {isEncalhe(p) ? (
                          <span className="inline-flex items-center gap-1.5 text-danger" title="Sem venda há mais de 90 dias">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                            {fmtDate(p.last_sale_at)}
                          </span>
                        ) : (
                          <span className="text-ink-faint">{fmtDate(p.last_sale_at)}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => handleReactivate(p.id, p.name)}
                            disabled={reactivating === p.id || deleting === p.id}
                            className="text-xs px-3 py-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 rounded text-white"
                          >
                            {reactivating === p.id ? 'Reativando...' : 'Reativar'}
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(p.id, p.name, p.sku)}
                              disabled={reactivating === p.id || deleting === p.id}
                              className="text-xs px-3 py-1 bg-red-800 hover:bg-red-700 disabled:opacity-50 rounded text-white"
                              title="Excluir definitivamente (some de todas as telas; histórico preservado no banco)"
                            >
                              {deleting === p.id ? 'Excluindo...' : 'Excluir'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {sorted.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-ink-faint">
                      {products.length === 0
                        ? 'Nenhum produto inativo. Tudo ativo no Painel BX.'
                        : 'Nenhum produto encontrado com esse filtro.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}

function sortValue(p: { sku: string; name: string; suppliers?: { name: string | null } | null; current_stock: number; cost: number; last_sale_at: string | null }, key: SortKey): string | number {
  switch (key) {
    case 'sku': return p.sku
    case 'name': return p.name.toLowerCase()
    case 'supplier': return (p.suppliers?.name ?? '').toLowerCase()
    case 'current_stock': return p.current_stock
    case 'cost': return p.cost
    case 'last_sale_at': return p.last_sale_at ? new Date(p.last_sale_at).getTime() : 0
  }
}

interface SortHeaderProps {
  label: string
  align: 'left' | 'right'
  active: boolean
  dir: 'asc' | 'desc'
  onClick: () => void
}

function SortHeader({ label, align, active, dir, onClick }: SortHeaderProps) {
  return (
    <th
      onClick={onClick}
      className={`text-${align} text-xs font-semibold uppercase tracking-wider px-4 py-2.5 bg-sunken cursor-pointer select-none transition-colors hover:bg-line ${
        active ? 'text-brand-300' : 'text-ink-muted'
      }`}
      title="Clique para ordenar"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[10px] ${active ? 'opacity-100' : 'opacity-30'}`}>
          {active ? (dir === 'asc' ? '▲' : '▼') : '▲'}
        </span>
      </span>
    </th>
  )
}
